<script setup lang="ts">
import { useAppStore } from '~/stores/AppStore'
import { useWorkStore } from '~/stores/WorkStore'
import type { SectionWorkListRecord } from '~/types/datocms'
import { CardType } from '~/types'

interface SectionWorksProps {
  data: SectionWorkListRecord
  moduleIndex: number
}

type ListView = 'work' | 'reel'

const props = defineProps<SectionWorksProps>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)

const appConfig = useAppConfig()
const { isDesktop } = useDevice()
const route = useRoute()
const store = useWorkStore()
const appStore = useAppStore()
const listView = ref<ListView>('work')

// Set pages per data load
store.setItemsPerPage(props.data.itemsPerPage as number)

function loadData(initial: boolean = true) {
  // Handle initial load vs. loadMore
  if (initial) {
    store.setLoading(true)
    store.resetWorks()
  } else {
    store.setLoadingMore(true)
  }

  if (route.query.category) {
    if (route.query.category === 'reel') {
      listView.value = 'reel'
      useTimeoutFn(() => {
        store.setLoading(false)
      }, 600)
    } else {
      listView.value = 'work'
      // Fetch Works by Category
      const cat = store.getCategoryBySlug(route.query.category as string)

      if (cat) {
        store.pushFilter(cat)
        // Filtered by Category (DatoCMS record ID)
        store.fetchWorks(cat.id)
      }
    }
  } else {
    listView.value = 'work'
    // Unfiltered / All Work
    store.pushFilter(null)
    store.fetchWorks()
  }
}

watch(
  () => route.query,
  () => {
    loadData()
  }
)

onMounted(() => {
  if (!store.allWorks.length || route.query.category !== store.filteredBy?.slug) {
    loadData()
  }
})
</script>

<template>
  <div class="w-full overflow-hidden">
    <ClientOnly v-if="appConfig.ui.enableFluidThree">
      <FluidWhite />
    </ClientOnly>
    <LazyDeliciousSausage
      v-if="isDesktop"
      class="absolute -top-20 right-0 z-0 hidden translate-x-1/3 lg:block"
      name="works"
    />
    <div class="container container-max-width page-pt relative" :class="invertedStyles">
      <Heading
        v-if="data.title"
        class="mb-4 lg:mb-24"
        :size="1"
        :level="level"
        :is-semantic="isSemantic"
        ><TextReveal>{{ data.title }}</TextReveal></Heading
      >
      <WorkPageFilter v-if="data.categories.length" :categories="data.categories" />
      <template v-if="listView === 'work'">
        <WorkList
          class="min-h-screen"
          :data="store.allWorks"
          :is-loading="store.isLoading"
          :load-more-label="data.linkMore"
        />
        <div class="min-h-[124px] py-8 pb-12 text-center">
          <Transition
            enter-from-class="opacity-0 scale-75"
            leave-to-class="opacity-0 scale-75"
            enter-active-class="transition-all duration-500"
            leave-active-class="transition-all duration-300"
          >
            <Button
              v-show="data.linkMore && store.isButtonVisible"
              :label="data.linkMore"
              :loading="store.isLoadingMore"
              class="min-w-[168px]"
              variant="outline"
              @click="loadData(false)"
            />
          </Transition>
        </div>
      </template>
      <template v-else-if="listView === 'reel'">
        <WorkList
          class="min-h-[585px]"
          :data="appStore.allReels"
          :type="CardType.REEL"
          :is-loading="store.isLoading"
          :load-more-label="data.linkMore"
        />
      </template>
    </div>
  </div>
</template>
